import React, { useCallback, useEffect, useState } from 'react';
import {
  FlaskConical,
  Plus,
  Trophy,
  Pin,
  PinOff,
  Archive,
  Edit3,
  X as XIcon,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { authFetch } from '../utils/authUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const STATUS_COLORS = {
  running: 'bg-indigo-500/20 text-indigo-200 border-indigo-500/40',
  promoted: 'bg-purple-500/20 text-purple-200 border-purple-500/40',
  archived: 'bg-slate-700 text-gray-400 border-slate-600',
};

const emptyForm = () => ({
  experiment_id: '',
  name: '',
  description: '',
  location: '',
  variant_a_json: JSON.stringify({ title: 'Variant A copy' }, null, 2),
  variant_b_json: JSON.stringify({ title: 'Variant B copy' }, null, 2),
});

const AdminExperiments = () => {
  const [experiments, setExperiments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [banner, setBanner] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [expandedId, setExpandedId] = useState(null);
  // analyticsMap: { [experiment_id]: { variants, winner, can_auto_promote, pinned_variant } }
  const [analyticsMap, setAnalyticsMap] = useState({});

  // ── Data loading ────────────────────────────────────────────────────────
  const loadExperiments = useCallback(async () => {
    setLoading(true);
    try {
      const r = await authFetch(`${BACKEND_URL}/api/admin/experiments?include_archived=true`);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      setExperiments(d.experiments || []);
    } catch (e) {
      setBanner({ kind: 'error', text: String(e.message || e) });
    } finally {
      setLoading(false);
    }
  }, []);

  const loadAnalytics = useCallback(async (experimentId) => {
    try {
      const r = await authFetch(
        `${BACKEND_URL}/api/admin/experiments/${experimentId}/analytics?window_days=30`
      );
      if (!r.ok) return;
      const d = await r.json();
      setAnalyticsMap((prev) => ({ ...prev, [experimentId]: d }));
    } catch { /* silent */ }
  }, []);

  useEffect(() => { loadExperiments(); }, [loadExperiments]);

  // Auto-clear banner
  useEffect(() => {
    if (!banner) return undefined;
    const id = setTimeout(() => setBanner(null), 4000);
    return () => clearTimeout(id);
  }, [banner]);

  // ── CRUD actions ────────────────────────────────────────────────────────
  const startEdit = (exp) => {
    setEditingId(exp.experiment_id);
    setShowCreate(true);
    setForm({
      experiment_id: exp.experiment_id,
      name: exp.name || '',
      description: exp.description || '',
      location: exp.location || '',
      variant_a_json: JSON.stringify(exp.variants?.A || {}, null, 2),
      variant_b_json: JSON.stringify(exp.variants?.B || {}, null, 2),
    });
  };

  const cancelForm = () => {
    setShowCreate(false);
    setEditingId(null);
    setForm(emptyForm());
  };

  const submitForm = async () => {
    let vA, vB;
    try {
      vA = JSON.parse(form.variant_a_json);
      vB = JSON.parse(form.variant_b_json);
    } catch (e) {
      setBanner({ kind: 'error', text: `Variant JSON is invalid: ${e.message}` });
      return;
    }
    const body = {
      name: form.name.trim(),
      description: form.description,
      location: form.location,
      variants: { A: vA, B: vB },
    };
    try {
      let r;
      if (editingId) {
        r = await authFetch(`${BACKEND_URL}/api/admin/experiments/${editingId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
      } else {
        body.experiment_id = form.experiment_id.trim();
        r = await authFetch(`${BACKEND_URL}/api/admin/experiments`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
      }
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data?.detail || `HTTP ${r.status}`);
      setBanner({
        kind: 'success',
        text: editingId ? `Updated "${editingId}"` : `Created "${body.experiment_id}"`,
      });
      cancelForm();
      await loadExperiments();
    } catch (e) {
      setBanner({ kind: 'error', text: String(e.message || e) });
    }
  };

  const archive = async (experimentId) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Archive experiment "${experimentId}"? Users will stop seeing it.`)) return;
    try {
      const r = await authFetch(`${BACKEND_URL}/api/admin/experiments/${experimentId}`, {
        method: 'DELETE',
      });
      if (!r.ok) {
        const b = await r.json().catch(() => ({}));
        throw new Error(b?.detail || `HTTP ${r.status}`);
      }
      setBanner({ kind: 'success', text: `Archived "${experimentId}"` });
      await loadExperiments();
    } catch (e) {
      setBanner({ kind: 'error', text: String(e.message || e) });
    }
  };

  const promote = async (experimentId, variant, force = false) => {
    try {
      const r = await authFetch(
        `${BACKEND_URL}/api/admin/experiments/${experimentId}/promote`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ variant, force, window_days: 30 }),
        }
      );
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        if (r.status === 400 && !force) {
          // eslint-disable-next-line no-alert
          const ok = window.confirm(`${data.detail}\n\nPromote anyway?`);
          if (ok) return promote(experimentId, variant, true);
          setBanner({ kind: 'error', text: data.detail || 'Promote failed' });
          return;
        }
        throw new Error(data?.detail || `HTTP ${r.status}`);
      }
      setBanner({ kind: 'success', text: `Variant ${variant} pinned for "${experimentId}"` });
      await loadExperiments();
      await loadAnalytics(experimentId);
    } catch (e) {
      setBanner({ kind: 'error', text: String(e.message || e) });
    }
  };

  const unpin = async (experimentId) => {
    try {
      const r = await authFetch(
        `${BACKEND_URL}/api/admin/experiments/${experimentId}/pin`,
        { method: 'DELETE' }
      );
      if (!r.ok) {
        const b = await r.json().catch(() => ({}));
        throw new Error(b?.detail || `HTTP ${r.status}`);
      }
      setBanner({ kind: 'success', text: `Unpinned "${experimentId}" — back to 50/50` });
      await loadExperiments();
      await loadAnalytics(experimentId);
    } catch (e) {
      setBanner({ kind: 'error', text: String(e.message || e) });
    }
  };

  const toggleExpand = async (experimentId) => {
    if (expandedId === experimentId) { setExpandedId(null); return; }
    setExpandedId(experimentId);
    if (!analyticsMap[experimentId]) await loadAnalytics(experimentId);
  };

  return (
    <Card className="bg-slate-800/50 border-slate-700" data-testid="admin-experiments">
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <CardTitle className="text-white flex items-center gap-2">
            <FlaskConical className="w-5 h-5 text-fuchsia-400" />
            Experiments
          </CardTitle>
          <Button
            size="sm"
            onClick={() => { cancelForm(); setShowCreate(true); }}
            className="bg-fuchsia-600 hover:bg-fuchsia-500 text-white"
            data-testid="experiments-new"
          >
            <Plus className="w-4 h-4 mr-1" /> New experiment
          </Button>
        </div>
        <p className="text-xs text-gray-400 mt-1">
          Define A/B copy tests without deploying code. Consume from the frontend with <code className="px-1 bg-slate-900 rounded">useExperiment(id)</code>.
        </p>
      </CardHeader>

      <CardContent>
        {banner && (
          <div
            className={`mb-3 px-3 py-2 rounded-md text-xs ${
              banner.kind === 'success'
                ? 'bg-emerald-500/15 border border-emerald-500/40 text-emerald-200'
                : 'bg-rose-500/15 border border-rose-500/40 text-rose-200'
            }`}
            data-testid="experiments-banner"
          >
            {banner.text}
          </div>
        )}

        {/* Create / edit form */}
        {showCreate && (
          <div className="mb-5 rounded-xl bg-slate-900/60 border border-fuchsia-500/30 p-4" data-testid="experiments-form">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-semibold text-white">
                {editingId ? `Edit "${editingId}"` : 'New experiment'}
              </p>
              <button
                type="button"
                onClick={cancelForm}
                className="text-gray-400 hover:text-white"
                data-testid="experiments-form-close"
              >
                <XIcon className="w-4 h-4" />
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {!editingId && (
                <label className="text-xs text-gray-400 block col-span-full sm:col-span-1">
                  Experiment ID (slug)
                  <input
                    type="text"
                    value={form.experiment_id}
                    onChange={(e) => setForm({ ...form, experiment_id: e.target.value })}
                    className="mt-1 w-full bg-slate-800 border border-slate-700 rounded px-2 py-1.5 text-sm text-white"
                    placeholder="upload_cta_copy"
                    data-testid="experiments-form-id"
                  />
                </label>
              )}
              <label className="text-xs text-gray-400 block">
                Name
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="mt-1 w-full bg-slate-800 border border-slate-700 rounded px-2 py-1.5 text-sm text-white"
                  placeholder="Upload CTA copy"
                  data-testid="experiments-form-name"
                />
              </label>
              <label className="text-xs text-gray-400 block col-span-full">
                Where it's used (admin note)
                <input
                  type="text"
                  value={form.location}
                  onChange={(e) => setForm({ ...form, location: e.target.value })}
                  className="mt-1 w-full bg-slate-800 border border-slate-700 rounded px-2 py-1.5 text-sm text-white"
                  placeholder="Library > Personal > Upload tab"
                  data-testid="experiments-form-location"
                />
              </label>
              <label className="text-xs text-gray-400 block col-span-full">
                Description
                <textarea
                  rows={2}
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="mt-1 w-full bg-slate-800 border border-slate-700 rounded px-2 py-1.5 text-sm text-white"
                  placeholder="What are we testing and why?"
                  data-testid="experiments-form-description"
                />
              </label>
              <label className="text-xs text-gray-400 block">
                Variant A (JSON payload)
                <textarea
                  rows={5}
                  value={form.variant_a_json}
                  onChange={(e) => setForm({ ...form, variant_a_json: e.target.value })}
                  className="mt-1 w-full bg-slate-800 border border-slate-700 rounded px-2 py-1.5 text-xs font-mono text-white"
                  data-testid="experiments-form-variant-a"
                />
              </label>
              <label className="text-xs text-gray-400 block">
                Variant B (JSON payload)
                <textarea
                  rows={5}
                  value={form.variant_b_json}
                  onChange={(e) => setForm({ ...form, variant_b_json: e.target.value })}
                  className="mt-1 w-full bg-slate-800 border border-slate-700 rounded px-2 py-1.5 text-xs font-mono text-white"
                  data-testid="experiments-form-variant-b"
                />
              </label>
            </div>
            <div className="flex justify-end gap-2 mt-3">
              <Button size="sm" variant="ghost" onClick={cancelForm}>Cancel</Button>
              <Button
                size="sm"
                onClick={submitForm}
                className="bg-fuchsia-600 hover:bg-fuchsia-500 text-white"
                data-testid="experiments-form-submit"
              >
                {editingId ? 'Save changes' : 'Create experiment'}
              </Button>
            </div>
          </div>
        )}

        {/* List */}
        {loading ? (
          <p className="text-sm text-gray-400">Loading…</p>
        ) : experiments.length === 0 ? (
          <p className="text-sm text-gray-500 italic">
            No experiments yet. Click <span className="text-fuchsia-300">New experiment</span> to define your first A/B test.
          </p>
        ) : (
          <ul className="space-y-3">
            {experiments.map((exp) => {
              const stats = analyticsMap[exp.experiment_id];
              const isExpanded = expandedId === exp.experiment_id;
              const isArchived = exp.status === 'archived';
              return (
                <li
                  key={exp.experiment_id}
                  className={`rounded-lg border ${
                    isArchived
                      ? 'bg-slate-900/20 border-slate-700/40 opacity-70'
                      : 'bg-slate-900/40 border-slate-700/60'
                  }`}
                  data-testid={`experiment-card-${exp.experiment_id}`}
                >
                  <div className="p-3 flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm font-semibold text-white truncate">{exp.name}</p>
                        <span className={`text-[9px] font-mono font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border ${STATUS_COLORS[exp.status] || STATUS_COLORS.running}`}>
                          {exp.status}
                        </span>
                        {exp.pinned_variant && (
                          <span className="flex items-center gap-1 text-[9px] font-mono font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-purple-500/20 text-purple-200 border border-purple-500/40">
                            <Pin className="w-2.5 h-2.5" /> {exp.pinned_variant} pinned
                          </span>
                        )}
                      </div>
                      <p className="text-[11px] text-gray-500 font-mono mt-0.5 truncate">{exp.experiment_id}</p>
                      {exp.location && (
                        <p className="text-[11px] text-gray-500 italic mt-0.5">📍 {exp.location}</p>
                      )}
                      {exp.description && (
                        <p className="text-xs text-gray-400 mt-1 line-clamp-2">{exp.description}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {!isArchived && (
                        <button
                          type="button"
                          onClick={() => startEdit(exp)}
                          className="p-1.5 rounded text-gray-400 hover:text-white hover:bg-slate-700"
                          aria-label="Edit"
                          data-testid={`experiment-edit-${exp.experiment_id}`}
                        >
                          <Edit3 className="w-3.5 h-3.5" />
                        </button>
                      )}
                      {!isArchived && (
                        <button
                          type="button"
                          onClick={() => archive(exp.experiment_id)}
                          className="p-1.5 rounded text-gray-400 hover:text-rose-300 hover:bg-slate-700"
                          aria-label="Archive"
                          data-testid={`experiment-archive-${exp.experiment_id}`}
                        >
                          <Archive className="w-3.5 h-3.5" />
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => toggleExpand(exp.experiment_id)}
                        className="p-1.5 rounded text-gray-400 hover:text-white hover:bg-slate-700"
                        aria-label="Expand"
                        data-testid={`experiment-toggle-${exp.experiment_id}`}
                      >
                        {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="px-3 pb-3 border-t border-slate-700/40 pt-3">
                      {/* Variant previews */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
                        {['A', 'B'].map((arm) => {
                          const s = stats?.variants?.[arm] || { exposed: 0, converted: 0, pct: 0 };
                          const isArmPinned = exp.pinned_variant === arm;
                          const isWinner = stats?.winner === arm;
                          return (
                            <div
                              key={arm}
                              className={`rounded border p-2 ${
                                isArmPinned
                                  ? 'bg-purple-500/10 border-purple-500/50'
                                  : isWinner
                                  ? 'bg-emerald-500/10 border-emerald-500/40'
                                  : 'bg-slate-900/60 border-slate-700/60'
                              }`}
                            >
                              <div className="flex items-center justify-between">
                                <span className={`text-[10px] font-mono font-bold uppercase ${
                                  isArmPinned ? 'text-purple-200' : isWinner ? 'text-emerald-300' : 'text-gray-500'
                                }`}>
                                  Variant {arm}
                                  {isArmPinned ? ' 📌' : isWinner ? ' ★' : ''}
                                </span>
                                <span className={`text-xs font-bold tabular-nums ${
                                  isArmPinned ? 'text-purple-200' : isWinner ? 'text-emerald-300' : 'text-gray-300'
                                }`}>
                                  {s.pct}%
                                </span>
                              </div>
                              <p className="text-[10px] text-gray-500 tabular-nums mb-1">
                                {s.converted}/{s.exposed} converted
                              </p>
                              <pre className="text-[10px] text-gray-400 bg-slate-900/60 rounded p-1 overflow-auto max-h-24 mb-1.5">
                                {JSON.stringify(exp.variants?.[arm] || {}, null, 2)}
                              </pre>
                              {!isArchived && !isArmPinned && (
                                <button
                                  type="button"
                                  onClick={() => promote(exp.experiment_id, arm)}
                                  className="w-full text-[10px] font-semibold px-2 py-1 rounded bg-indigo-600/70 hover:bg-indigo-500 text-white transition-colors"
                                  data-testid={`experiment-promote-${exp.experiment_id}-${arm}`}
                                >
                                  <Trophy className="w-2.5 h-2.5 inline mr-0.5" /> Promote {arm}
                                </button>
                              )}
                              {!isArchived && isArmPinned && (
                                <button
                                  type="button"
                                  onClick={() => unpin(exp.experiment_id)}
                                  className="w-full text-[10px] font-semibold px-2 py-1 rounded bg-purple-600/70 hover:bg-purple-500 text-white transition-colors"
                                  data-testid={`experiment-unpin-${exp.experiment_id}`}
                                >
                                  <PinOff className="w-2.5 h-2.5 inline mr-0.5" /> Unpin
                                </button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                      {stats && stats.winner === 'insufficient' && (
                        <p className="text-[10px] text-gray-500 italic">
                          Need ≥ 5 users per variant for a winner · ≥ 50/arm + ≥ 10% delta to auto-promote.
                        </p>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
};

export default AdminExperiments;
